import { useEffect, useRef, useState } from "react";
import FilePicker from "@/components/FilePicker";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import AccordionCard, { ExpandAllToggle } from "@/components/AccordionCard";
import {
  Plus, Trash, PencilSimple, DownloadSimple, FloppyDisk, X, UploadSimple, Note, PaperPlaneTilt,
} from "@phosphor-icons/react";

const fmtWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

async function download(fileId, filename) {
  try {
    const res = await api.get(`/files/${fileId}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "file"; a.click();
    URL.revokeObjectURL(url);
  } catch (_e) { toast.error("Download failed"); }
}

function NoteChecklist({ userId, note, onChanged }) {
  const items = note.checklist || [];
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const total = items.length;
  const doneN = items.filter(i => i.done).length;

  const add = async (e) => {
    e?.preventDefault?.();
    const t = title.trim();
    if (!t) return;
    setBusy(true);
    try {
      await api.post(`/admin/clients/${userId}/notes/${note.note_id}/checklist`,
        { title: t });
      setTitle(""); setAdding(false);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };
  const toggle = async (item) => {
    try {
      await api.patch(
        `/admin/clients/${userId}/notes/${note.note_id}/checklist/${item.item_id}`,
        { done: !item.done });
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const del = async (item) => {
    try {
      await api.delete(
        `/admin/clients/${userId}/notes/${note.note_id}/checklist/${item.item_id}`);
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };

  return (
    <div className="mt-3" data-testid={`note-checklist-${note.note_id}`}>
      {total > 0 && (
        <div className="mb-2">
          <p className="label text-[10px] mb-1">
            Checklist · {doneN}/{total} complete
          </p>
          <ul className="space-y-1">
            {items.map(item => (
              <li key={item.item_id}
                data-testid={`note-cli-${item.item_id}`}
                className="flex items-start gap-2 text-sm">
                <input type="checkbox" checked={!!item.done}
                  onChange={() => toggle(item)}
                  data-testid={`note-cli-toggle-${item.item_id}`}
                  className="mt-1 cursor-pointer" />
                <span className="flex-1"
                  style={{
                    textDecoration: item.done ? "line-through" : "none",
                    color: item.done ? "var(--text-dim)" : "var(--text)",
                  }}>
                  {item.title}
                </span>
                <button type="button" onClick={() => del(item)}
                  data-testid={`note-cli-del-${item.item_id}`}
                  className="shrink-0 p-1 hover:opacity-70"
                  style={{ background: "transparent", border: "none", color: "var(--text-dim)" }}
                  title="Delete item">
                  <Trash size={10} weight="bold" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {adding ? (
        <form onSubmit={add} className="flex gap-2 items-center">
          <input autoFocus required value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid={`note-cli-input-${note.note_id}`}
            placeholder="Actionable item…"
            className="flex-1 px-3 py-2 text-sm" />
          <button type="submit" disabled={busy || !title.trim()}
            data-testid={`note-cli-add-${note.note_id}`}
            className="btn-primary text-xs h-9">
            Add
          </button>
          <button type="button"
            onClick={() => { setAdding(false); setTitle(""); }}
            className="btn-ghost text-xs">Cancel</button>
        </form>
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          data-testid={`note-cli-start-${note.note_id}`}
          className="btn-ghost text-xs">
          <Plus size={11} weight="bold" /> Add checklist item
        </button>
      )}
    </div>
  );
}

function NoteCard({ userId, note, onChanged, forceOpen }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.content || "");
  const filesRef = useRef(null);
  const isDraft = !!note.is_draft;

  const save = async (nextIsDraft) => {
    try {
      const patch = { content: draft };
      if (nextIsDraft !== undefined) patch.is_draft = nextIsDraft;
      await api.patch(`/admin/clients/${userId}/notes/${note.note_id}`, patch);
      toast.success(nextIsDraft === false ? "Note published" : "Saved");
      setEditing(false); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const publish = async () => {
    try {
      await api.patch(`/admin/clients/${userId}/notes/${note.note_id}`,
        { content: note.content || "", is_draft: false });
      toast.success("Note published");
      onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm(isDraft ? "Delete this draft?" : "Delete this note?")) return;
    try {
      await api.delete(`/admin/clients/${userId}/notes/${note.note_id}`);
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };
  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin/clients/${userId}/notes/${note.note_id}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Uploaded"); onChanged();
    } catch (_e) { toast.error("Upload failed"); }
  };
  const delFile = async (fid) => {
    try {
      await api.delete(`/admin/clients/${userId}/notes/${note.note_id}/files/${fid}`);
      onChanged();
    } catch (_e) { toast.error("Failed"); }
  };

  const header = (
    <div>
      <div className="flex flex-wrap items-baseline gap-3">
        <p className="title-xl text-base" style={{ fontWeight: 700 }}>
          {fmtWhen(note.created_at)}
        </p>
        <span className="label" style={{ color: "var(--text-dim)" }}>{note.created_by}</span>
        {isDraft && (
          <span className="label px-2 py-0.5"
            data-testid={`note-draft-badge-${note.note_id}`}
            style={{ background: "#fef3c7", color: "#78350f", border: "1px solid #fde68a", fontWeight: 700 }}>
            DRAFT
          </span>
        )}
        {(note.files || []).length > 0 && (
          <span className="label" style={{ color: "var(--text-dim)" }}>
            · {note.files.length} file{note.files.length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <p className="text-sm mt-1 truncate" style={{ color: "var(--text-dim)" }}>
        {(note.content || "").split("\n")[0]}
      </p>
    </div>
  );

  return (
    <AccordionCard testid={`note-card-${note.note_id}`} header={header} forceOpen={forceOpen}>
      {editing ? (
        <div className="space-y-2">
          <textarea value={draft} rows={4}
            data-testid={`note-edit-textarea-${note.note_id}`}
            onChange={(e) => setDraft(e.target.value)}
            className="w-full px-3 py-2 text-sm" />
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save(isDraft ? true : undefined)} className="btn-primary text-xs"
              data-testid={`note-save-${note.note_id}`}>
              <FloppyDisk size={12} weight="bold" /> {isDraft ? "Save draft" : "Save"}
            </button>
            {isDraft && (
              <button onClick={() => save(false)} className="btn-primary text-xs"
                data-testid={`note-save-publish-${note.note_id}`}>
                <PaperPlaneTilt size={12} weight="bold" /> Save & publish
              </button>
            )}
            <button onClick={() => { setEditing(false); setDraft(note.content || ""); }}
              className="btn-ghost text-xs">
              <X size={12} /> Cancel
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{note.content}</p>
      )}

      {(note.files || []).length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {note.files.map(f => (
            <span key={f.file_id} className="inline-flex items-center gap-1"
              data-testid={`note-file-${f.file_id}`}>
              <button type="button"
                onClick={() => download(f.file_id, f.filename)}
                className="btn-ghost text-xs">
                <DownloadSimple size={11} weight="bold" /> {f.filename}
              </button>
              <button type="button" onClick={() => delFile(f.file_id)}
                className="btn-ghost text-xs p-1"
                data-testid={`note-file-del-${f.file_id}`}>
                <Trash size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      <NoteChecklist userId={userId} note={note} onChanged={onChanged} />

      <div className="mt-4 flex flex-wrap gap-2">
        {!editing && (
          <button onClick={() => setEditing(true)} className="btn-ghost text-xs"
            data-testid={`note-edit-${note.note_id}`}>
            <PencilSimple size={12} /> Edit
          </button>
        )}
        {!editing && isDraft && (
          <button onClick={publish} className="btn-primary text-xs"
            data-testid={`note-publish-${note.note_id}`}>
            <PaperPlaneTilt size={12} weight="bold" /> Publish
          </button>
        )}
        <label className="btn-ghost text-xs cursor-pointer"
          data-testid={`note-upload-label-${note.note_id}`}>
          <UploadSimple size={12} weight="bold" /> Attach files
          <input ref={filesRef} type="file" multiple className="hidden"
            data-testid={`note-upload-${note.note_id}`}
            onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
        </label>
        <button onClick={del} className="btn-ghost text-xs"
          data-testid={`note-delete-${note.note_id}`}>
          <Trash size={12} /> Delete
        </button>
      </div>
    </AccordionCard>
  );
}

function NewNoteForm({ userId, onCreated }) {
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [autoStatus, setAutoStatus] = useState("idle"); // "idle" | "saving" | "saved"
  const filesRef = useRef(null);
  const debounceRef = useRef(null);
  const contentRef = useRef(content);
  const draftIdRef = useRef(draftId);
  const userIdRef = useRef(userId);
  useEffect(() => { contentRef.current = content; }, [content]);
  useEffect(() => { draftIdRef.current = draftId; }, [draftId]);
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // Reset when switching clients
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);
  useEffect(() => {
    // If admin switches to a different client, forget the previous draft handle.
    setDraftId(null); setContent(""); setAutoStatus("idle");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const autoSave = async () => {
    const c = (contentRef.current || "").trim();
    if (!c) return;
    setAutoStatus("saving");
    try {
      if (draftIdRef.current) {
        await api.patch(
          `/admin/clients/${userIdRef.current}/notes/${draftIdRef.current}`,
          { content: contentRef.current, is_draft: true });
      } else {
        const { data: created } = await api.post(
          `/admin/clients/${userIdRef.current}/notes`,
          { content: contentRef.current, is_draft: true });
        setDraftId(created.note_id);
        // Refresh the list so the new draft card appears with its DRAFT badge.
        onCreated();
      }
      setAutoStatus("saved");
    } catch { setAutoStatus("idle"); }
  };

  const scheduleAutoSave = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setAutoStatus("idle");
    debounceRef.current = setTimeout(autoSave, 2000);
  };

  const onContentChange = (e) => {
    setContent(e.target.value);
    if (e.target.value.trim()) scheduleAutoSave();
  };

  const submit = async (asDraft) => {
    if (!content.trim()) { toast.error("Enter a note"); return; }
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    setBusy(true);
    try {
      let noteId = draftId;
      if (noteId) {
        // Promote the existing draft (or keep it as draft).
        await api.patch(`/admin/clients/${userId}/notes/${noteId}`,
          { content, is_draft: !!asDraft });
      } else {
        const { data: created } = await api.post(
          `/admin/clients/${userId}/notes`,
          { content, is_draft: !!asDraft });
        noteId = created.note_id;
      }
      const files = Array.from(filesRef.current?.files || []);
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/admin/clients/${userId}/notes/${noteId}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success(asDraft ? "Draft saved" : "Note added");
      setContent(""); setDraftId(null); setAutoStatus("idle");
      if (filesRef.current) filesRef.current.value = "";
      onCreated();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={(e) => { e.preventDefault(); submit(false); }}
      className="panel p-5 space-y-3" data-testid="new-note-form">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Note size={16} weight="bold" />
          <h3 className="title-xl text-xl">Add note</h3>
        </div>
        <span className="label" data-testid="new-note-autosave-status"
          style={{ color: "var(--text-dim)" }}>
          {autoStatus === "saving" ? "Saving…" : autoStatus === "saved" ? "Auto-saved" : ""}
        </span>
      </div>
      <textarea value={content} rows={3}
        placeholder="What did you learn / discuss / decide?"
        data-testid="new-note-content"
        onChange={onContentChange}
        className="w-full px-3 py-2 text-sm" />
      <div>
        <p className="label mb-1">Attachments (optional, multiple)</p>
        <FilePicker ref={filesRef} testid="new-note-files" />
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="submit" disabled={busy} data-testid="new-note-submit"
          className="btn-primary justify-center flex-1 h-11">
          <Plus size={14} weight="bold" /> {busy ? "Saving…" : "Save note"}
        </button>
        <button type="button" disabled={busy} onClick={() => submit(true)}
          data-testid="new-note-save-draft"
          className="btn-ghost justify-center flex-1 h-11">
          <FloppyDisk size={14} weight="bold" /> Save as draft
        </button>
      </div>
    </form>
  );
}

export default function NotesSection({ userId }) {
  const [notes, setNotes] = useState([]);
  const [allOpen, setAllOpen] = useState(null);
  const load = async () => {
    try { const { data } = await api.get(`/admin/clients/${userId}/notes`); setNotes(data); }
    catch (_e) { /* silent */ }
  };
  useEffect(() => { load(); }, [userId]);

  return (
    <section className="space-y-4" data-testid="notes-section">
      <NewNoteForm userId={userId} onCreated={load} />
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="title-xl text-2xl">Notes</h2>
        <div className="flex items-center gap-3">
          {notes.length > 1 && (
            <ExpandAllToggle testid="notes-expand-all"
              allOpen={allOpen === true}
              onToggle={(next) => setAllOpen(next)} />
          )}
          <p className="label">{notes.length} note{notes.length === 1 ? "" : "s"}</p>
        </div>
      </div>
      {notes.length === 0 ? (
        <div className="panel p-8 text-center label">No notes yet</div>
      ) : (
        <div className="space-y-2">
          {notes.map(n => (
            <NoteCard key={n.note_id} userId={userId} note={n}
              forceOpen={allOpen === null ? undefined : allOpen}
              onChanged={load} />
          ))}
        </div>
      )}
    </section>
  );
}
